import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { MdAutoAwesome, MdRefresh } from 'react-icons/md';
import {
  BUDDIES_PAIR,
  BUDDY_HOME_ATTRIBUTE,
  Button,
  ConfirmDialog,
  DialogField,
  DialogFieldLabel,
  DialogTextarea,
  OrbBuddies,
  ORB_TIPS_ABOVE,
  CONFIRM_DIALOG_TONE_CAUTION,
  type BuddiesWho,
} from '@aibuild/ui';
import type { UserAppDraft } from '@api/appBuilder';
import { BUDDY_HOME_THUMBNAIL, THUMBNAIL_BUDDY_PX, THUMBNAIL_DIRECTION_MAX_CHARS } from './builderWorkspace.constants';
import { useCoverField } from './useCoverField';
import { CoverChoice, CoverImage, CoverRow, Frame, FrameImage, GenerateRow, Note } from './CoverField.styled';

interface CoverFieldProps {
  readonly draft: UserAppDraft;
  readonly onReload: () => void;
  readonly buddy?: BuddiesWho;
}

const GENERATING_TIPS = [
  { id: 'apps.builder.thumbnail.tip.reading', defaultMessage: 'Reading what your app does…' },
  { id: 'apps.builder.thumbnail.tip.dark', defaultMessage: 'Rendering the dark version first.' },
  { id: 'apps.builder.thumbnail.tip.light', defaultMessage: 'Then the same object on white.' },
  { id: 'apps.builder.thumbnail.tip.direction', defaultMessage: 'Want something specific? Describe it below.' },
];

const CoverField: React.FC<CoverFieldProps> = ({ draft, onReload, buddy = BUDDIES_PAIR }) => {
  const intl = useIntl();
  const light = Boolean(useTheme().isLightTheme);
  const cover = useCoverField(draft, onReload);
  const { thumbnail } = cover;
  const shown = draft.preview.presentation;
  const picture = (light ? shown?.coverLight : shown?.cover) ?? shown?.cover ?? null;
  const tips = useMemo(() => GENERATING_TIPS.map((tip) => intl.formatMessage(tip)), [intl]);

  return (
    <DialogField data-testid="app-builder__cover">
      <DialogFieldLabel as="span">
        {intl.formatMessage({ id: 'apps.builder.details.thumbnail', defaultMessage: 'Thumbnail' })}
      </DialogFieldLabel>
      <Frame {...{ [BUDDY_HOME_ATTRIBUTE]: BUDDY_HOME_THUMBNAIL }} data-testid="app-builder__thumbnail">
        {thumbnail.generating ? (
          <OrbBuddies
            size={THUMBNAIL_BUDDY_PX}
            who={buddy}
            busy
            network={false}
            tips={tips}
            tipPlacement={ORB_TIPS_ABOVE}
            dataTestId="app-builder__thumbnail-buddies"
          />
        ) : (
          picture && <FrameImage src={picture} alt="" />
        )}
      </Frame>
      {thumbnail.available && (
        <>
          <DialogTextarea
            rows={2}
            value={cover.direction}
            maxLength={THUMBNAIL_DIRECTION_MAX_CHARS}
            disabled={thumbnail.generating}
            onChange={(event) => cover.setDirection(event.target.value)}
            placeholder={intl.formatMessage({
              id: 'apps.builder.thumbnail.direction',
              defaultMessage: 'Optional: describe the picture you want, e.g. "a weld-neck flange with bolts"',
            })}
            aria-label={intl.formatMessage({ id: 'apps.builder.thumbnail.directionLabel', defaultMessage: 'Thumbnail description' })}
            data-testid="app-builder__thumbnail-direction"
          />
          <GenerateRow>
            <Button
              type="button"
              variant="outlined"
              size="sm"
              leadingIcon={
                cover.hasThumbnail ? <MdRefresh size={16} aria-hidden="true" /> : <MdAutoAwesome size={16} aria-hidden="true" />
              }
              loading={thumbnail.generating}
              disabled={thumbnail.generating}
              onClick={cover.generate}
              dataTestId="app-builder__thumbnail-generate"
            >
              {cover.hasThumbnail
                ? intl.formatMessage({ id: 'apps.builder.thumbnail.regenerate', defaultMessage: 'Regenerate' })
                : intl.formatMessage({ id: 'apps.builder.thumbnail.generate', defaultMessage: 'Generate' })}
            </Button>
            {thumbnail.credits !== null && (
              <Note data-testid="app-builder__thumbnail-cost">
                {intl.formatMessage(
                  { id: 'apps.builder.thumbnail.cost', defaultMessage: 'Uses {credits} credits.' },
                  { credits: intl.formatNumber(thumbnail.credits, { maximumFractionDigits: 2 }) },
                )}
              </Note>
            )}
          </GenerateRow>
        </>
      )}
      <DialogFieldLabel as="span" id="app-builder-cover">
        {intl.formatMessage({ id: 'apps.builder.details.plainCover', defaultMessage: 'Or a plain cover' })}
      </DialogFieldLabel>
      <CoverRow role="radiogroup" aria-labelledby="app-builder-cover">
        {cover.presets.map((preset) => {
          const selected = !cover.hasThumbnail && preset.id === cover.selectedPreset;
          return (
            <CoverChoice
              key={preset.id}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={preset.id}
              $selected={selected}
              disabled={cover.saving}
              onClick={() => cover.pickPreset(preset.id)}
              data-testid={`app-builder__cover-${preset.id}`}
            >
              <CoverImage src={light ? preset.coverLight : preset.cover} alt="" />
            </CoverChoice>
          );
        })}
      </CoverRow>
      <ConfirmDialog
        open={cover.replacing}
        onClose={cover.cancelReplace}
        onConfirm={cover.confirmReplace}
        tone={CONFIRM_DIALOG_TONE_CAUTION}
        title={intl.formatMessage({ id: 'apps.builder.thumbnail.replace.title', defaultMessage: 'Replace the AI thumbnail?' })}
        description={intl.formatMessage({
          id: 'apps.builder.thumbnail.replace.body',
          defaultMessage: 'The generated thumbnail will be deleted. Getting it back means generating, and paying for, a new one.',
        })}
        confirmLabel={intl.formatMessage({ id: 'apps.builder.thumbnail.replace.confirm', defaultMessage: 'Replace' })}
        dataTestId="app-builder__thumbnail-replace-dialog"
      />
    </DialogField>
  );
};

export default CoverField;
